import { Timestamp } from "firebase/firestore";

export const TOUR_PERMISSIONS = [
  "tour.manage",
  "tour.delete",
  "members.manage",
  "seasons.manage",
  "events.create",
  "events.edit",
  "events.delete",
  "matches.manage",
  "scores.enter.any",
  "scores.finalize",
  "handicaps.edit",
  "articles.manage",
  "achievements.manage"
] as const;

export type TourPermission = typeof TOUR_PERMISSIONS[number];

export type TourRole =
  | "OWNER"
  | "COMMISSIONER"
  | "SCOREKEEPER"
  | "EVENT_MANAGER"
  | "MEMBER"
  | "GUEST";

export type TourMembershipStatus = "INVITED" | "ACTIVE" | "REMOVED";

export type TourPermissionSet = Partial<Record<TourPermission, boolean>>;

export default interface TourMembership {
  userId: string;
  playerId?: string;
  status: TourMembershipStatus;
  role: TourRole;
  permissions: TourPermissionSet;
  joinedAt?: Timestamp;
  invitedAt?: Timestamp;
  invitedBy?: string;
  updatedAt: Timestamp;
}

export function hasTourPermission(
  membership: TourMembership | null | undefined,
  permission: TourPermission
) {
  return (
    membership?.status === "ACTIVE" &&
    membership.permissions[permission] === true
  );
}

export function getRolePermissions(role: TourRole): TourPermissionSet {
  const allPermissions = Object.fromEntries(
    TOUR_PERMISSIONS.map(permission => [permission, true])
  ) as TourPermissionSet;

  switch (role) {
    case "OWNER":
      return allPermissions;
    case "COMMISSIONER":
      return {
        ...allPermissions,
        "tour.delete": false
      };
    case "EVENT_MANAGER":
      return {
        "events.create": true,
        "events.edit": true,
        "matches.manage": true
      };
    case "SCOREKEEPER":
      return {
        "scores.enter.any": true,
        "scores.finalize": true,
        "handicaps.edit": true
      };
    case "MEMBER":
    case "GUEST":
      return {};
  }
}
